<template>
    <div class="py-10">
        <header>
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h2 class="py-0.5 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
                            {{ $t('IMPORT PERMIT HISTORY') }}
                        </h2>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            class="btn btn-blue shadow-sm rounded-md mr-4"
                            v-on:click="toggleModal()"
                        >
                            {{ $t('Apply For Import Permit') }}
                        </button>
                    </div>
                </div>
            </div>

        </header>
        <main>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-10 my-6 bg-white shadow overflow-hidden sm:rounded-md">
                    <loading :status="loading"/>
                    <div class="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200 sm:px-6">
                        <div>
                            <label class="sr-only" for="search">{{ $t('Search') }}</label>
                            <div class="flex rounded-md shadow-sm">
                                <div class="relative flex-grow focus-within:z-10">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        id="search"
                                        v-model.lazy="filters.search"
                                        :placeholder="$t('Search')"
                                        class="form-input block border-gray-300 w-full rounded-none rounded-l-md pl-10 text-sm transition ease-in-out duration-150"
                                        @change="getTickets"
                                    >
                                </div>
                                <div class="relative inline-flex rounded-none">
                                    <select
                                        id="status"
                                        v-model="filters.status"
                                        aria-label="Sort by"
                                        class="-mx-px block form-select w-full pl-3 pr-9 py-2 rounded-none border border-r-0 border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                        @change="getTickets"
                                    >
                                        <option :value="null">{{ $t('All Permits') }}</option>
                                        <template v-for="status in statusList">
                                            <option :value="status.id">{{ status.name }}</option>
                                        </template>
                                    </select>
                                </div>
                                <div class="relative inline-flex rounded-r-md">
                                    <button
                                        class="relative -ml-px inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700"
                                        type="button"
                                        @click="changeSort"
                                    >
                                        <svg-vue
                                            v-show="sort.order === 'asc'"
                                            class="h-5 w-5 text-gray-400"
                                            icon="font-awesome.sort-amount-down-alt-regular"
                                        ></svg-vue>
                                        <svg-vue
                                            v-show="sort.order === 'desc'"
                                            class="h-5 w-5 text-gray-400"
                                            icon="font-awesome.sort-amount-up-alt-regular"
                                        ></svg-vue>
                                        <span class="ml-2">{{ $t('Sort') }}</span>
                                    </button>
                                    <select
                                        id="sortBy"
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="-ml-px block form-select w-full pl-3 pr-9 py-2 rounded-l-none rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                        @change="changeSort"
                                    >
                                        <option value="subject">{{ $t('Subject') }}</option>
                                        <option value="status_id">{{ $t('Status') }}</option>
                                        <option value="created_at">{{ $t('Created at') }}</option>
                                        <option value="updated_at">{{ $t('Updated at') }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                    <template v-if="ticketList.length > 0">
                        <div class="-my-2 sm:-mx-6 lg:-mx-8">
                            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                <table class="min-w-full  divide-y divide-gray-200">
                                    <thead>
                                    <tr>
                                        <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-black uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                            <b>{{ $t('Subject') }}</b>
                                        </th>
                                        <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-black uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                            <b>{{ $t('Pharmacy Admin') }}</b>
                                        </th>
                                        <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-black uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                            <b>{{ $t('Pharmacy') }}</b>
                                        </th>
                                        <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-black uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                            <b>{{ $t('Created at') }}</b>
                                        </th>
                                        <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-black uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                            <b>{{ $t('Updated at') }}</b>
                                        </th>
                                        <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-black uppercase tracking-wider whitespace-no-wrap overflow-x-auto">
                                            <b>{{ $t('Status') }}</b>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white divide-y divide-gray-100">
                                    <template v-for="ticket in ticketList">
                                        <router-link
                                            :to="'/tickets/' + ticket.uuid"
                                            class="cursor-pointer hover:bg-gray-100 p"
                                            tag="tr"
                                        >
                                            <td class="px-6 py-4 max-w-0 w-full whitespace-no-wrap">
                                                <div class="w-full truncate text-sm leading-5 text-gray-900">
                                                    {{ ticket.subject }}
                                                </div>
                                            </td>
                                            <td class="px-6 py-4 max-w-0 w-full whitespace-no-wrap">
                                                <div class="w-full truncate text-sm leading-5 text-gray-900">
                                                    {{ticket.user.name}}
                                                </div>
                                            </td>
                                            <td class="px-6 py-4 max-w-0 w-full whitespace-no-wrap">
                                                <div class="w-full truncate text-sm leading-5 text-gray-900">
                                                    {{ticket.department.name}}
                                                </div>
                                            </td>
                                            <td class="px-6 py-4 whitespace-no-wrap leading-5">
                                                <div class="text-sm text-gray-800">
                                                    {{ ticket.created_at | momentFormatDate }}
                                                </div>
                                            </td>
                                            <td class="px-6 py-4 whitespace-no-wrap leading-5">
                                                <div class="text-sm text-gray-800">
                                                    {{ ticket.updated_at | momentFormatDateTimeAgo }}
                                                </div>
                                            </td>
                                            <td class="px-6 py-4 whitespace-no-wrap leading-5">
                                                <div class="text-sm text-gray-800">
                                                    {{ ticket.status.name }}
                                                </div>
                                            </td>
                                        </router-link>
                                    </template>
                                    </tbody>
                                </table>

                            </div>

                        </div>

                    </template>
                    <template v-else-if="!loading">
                        <div class="h-full flex">
                            <div class="m-auto">
                                <div class="grid grid-cols-1 justify-items-center h-full w-full py-24">
                                    <div class="flex justify-center items-center">
                                        <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
                                    </div>
                                    <div class="flex justify-center items-center">
                                        <div class="w-full font-semibold text-2xl">{{ $t('No records found') }}</div>
                                    </div>
                                    <template v-if="anyFilter">
                                        <div class="flex justify-center items-center">
                                            <div>{{ $t('Try changing the filters, or rephrasing your search') }}.</div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </template>
                    <nav class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div class="hidden sm:block">
                            <p class="text-sm leading-5 text-gray-700">
                                {{ $t('Showing') }}
                                <span class="font-medium">{{ (pagination.perPage * pagination.currentPage) - pagination.perPage + 1 }}</span>
                                {{ $t('to') }}
                                <span class="font-medium">{{ pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total }}</span>
                                {{ $t('of') }}
                                <span class="font-medium">{{ pagination.total }}</span>
                                {{ $t('results') }}
                            </p>
                        </div>
                        <div class="flex-1 flex justify-between sm:justify-end">
                            <button
                                :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
                                :disabled="pagination.currentPage <= 1"
                                class="pagination-link"
                                type="button"
                                @click="changePage(pagination.currentPage - 1)"
                            >
                                {{ $t('Previous') }}
                            </button>
                            <button
                                :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
                                :disabled="pagination.currentPage >= pagination.totalPages"
                                class="ml-3 pagination-link"
                                type="button"
                                @click="changePage(pagination.currentPage + 1)"
                            >
                                {{ $t('Next') }}
                            </button>
                        </div>
                    </nav>
                </div>
                <div class="slido">
                <div class="slideshow-container">

                    <div class="mySlides fade">
                        <div class="numbertext">1 / 3</div>
                        <img src="https://firebasestorage.googleapis.com/v0/b/lesodec-a5808.appspot.com/o/IM2.jpg?alt=media&token=6b4e7b32-f233-45d3-9cce-d926d65a885d" style="width:100%">
                        <div class="text">Caption Text</div>
                    </div>

                    <div class="mySlides fade">
                        <div class="numbertext">2 / 3</div>
                        <img src="https://firebasestorage.googleapis.com/v0/b/lesodec-a5808.appspot.com/o/IM3.jpg?alt=media&token=cd9d5783-d7ea-4bac-a426-074d965e0292" style="width:100%">
                        <div class="text">Caption Two</div>
                    </div>

                    <div class="mySlides fade">
                        <div class="numbertext">3 / 3</div>
                        <img src="https://firebasestorage.googleapis.com/v0/b/lesodec-a5808.appspot.com/o/IM4.jpg?alt=media&token=c32a6ea5-6263-4b31-9ae1-3089dddd9426" style="width:100%">
                        <div class="text">Caption Three</div>
                    </div>

                </div>
                <br>

                <div style="margin-left: 120px">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                </div>

                </div>


            </div>
        </main>
        <template>
            <div>
                <div v-if="showModal" class="overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none h-full w-full focus:outline-none justify-center items-center flex">
                    <div class="relative w-auto my-6 mx-auto max-w-6xl">
                        <!--content-->
                        <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                            <!--header-->
                            <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                                <h3 class="text-3xl font-semibold">
                                    Overleaf Notes on permit application
                                </h3>
                                <button class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none" v-on:click="toggleModal()">
                                         <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                                            ×
                                         </span>
                                </button>
                            </div>
                            <!--body-->
                            <div class="relative  p-6 flex-auto min-h-max bg-[url('/img/https://firebasestorage.googleapis.com/v0/b/lesodec-a5808.appspot.com/o/download.png?alt=media&token=b0ea4bae-10de-4226-ae92-18dcdede6f17')] bg-cover">
                                <p class="text-sm underline font-bold">Overleaf notes |||| Conditions of issue/renewal for export licence </p>
                                <p class="text-sm ">Consideration for application for issue/renewal may take advantage to impose any new conditions or
                                    insist on any aspects that have been previously overlooked or are brought to a new or legislation or
                                    policy. </p>
                                <p class="text-sm underline font-bold">Conditions for Premises </p>
                                <p class="text-sm "> - Compliance with minimum requirements </p>
                                <p class="text-sm "> - Valid local trader’s licence </p>
                                <p class="text-sm "> - No adverse report/s since the previous issue regarding e.g. wrongful dealing in medicines, lack
                                    of proper management and control of pharmaceutical business, etc </p>
                                <p class="text-sm "> - Appropriately registered pharmacist with practicing licence from the pharmacy council and
                                    without any recorded acts of professional misconduct over the previous years (acts that may or
                                    may not warrant revocation of practicing licence) </p>
                                <p class="text-sm "> -Appropriate renewal forms submitted well before previous licence lapsed </p>
                                <p class="text-sm "> - No changes to the previous conditions under which the licence was issued. </p>

                                <p class="text-sm underline font-bold">Conditions for Amendments </p>
                                <p class="text-sm "> - All amendments to conditions under which an export licence was issued must be formally applied for
                                    and approved. </p>
                                <p class="text-sm "> - An applicant must not affect any changes without prior approval except for situations where e.g. a
                                    registered pharmacist leaves without notice and a locum tenens is in attendance for a period less than
                                    four weeks, changes of directorship in a company without changes in the effective supervision of the
                                    business.ce </p>
                                <p class="text-sm "> - Changes of ownership, effective directorship of a company, structural changes to the premises, changes
                                    in effective supervision of business, relocation to another premise require prior and written approval.
                                </p>

                                <p class="text-sm underline font-bold">Validity  </p>
                                <p class="text-sm "> - ACertificates are valid for 6 months from time to time or until formally cancelled by the issuing authority.
                                    An annual renewal must be applied for each time an applicant wishes its licence to be renewed. </p>
                                <p class="text-sm "> - Once a licence has elapsed due to failure to renew by the licensee, there should not be a requirement
                                    for formal notification of intent by the authorizing authority to consider the licence invalid. </p>
                                <p class="text-sm "> - Suspension or cancellation of licence
                                </p>
                                <p class="text-sm "> A licence may be cancelled under the following situations:</p>
                                <p class="text-sm "> - Failure to comply with specific conditions of licensing</p>
                                <p class="text-sm "> - Wrongful dealing in medicines, herbal medicines and allied substances </p>
                                <p class="text-sm "> - Unauthorized change of premises </p>
                                <p class="text-sm "> -Failure to comply with other legislature e.g local authority licensing, health requirements that were part of the initial approval </p>
                                <p class="text-sm ">A formal and legislated process must be conducted prior to cancellation of a licence.</p>
                                <p class="text-sm "> ©2006 Lesotho Ministry of Health & Social Welfare</p>
                                <p class="text-sm "> All rights reserved. No part of this document may be reproduced or transmitted electronically in any
                                    form or by any means, electronic, mechanic, including photocopying, recording or any other
                                    information storage and retrieval system, without prior permission of the publisher. </p>
                            </div>
                        </div>
                        <!--footer-->
                        <div class="flex items-center justify-end border-t border-solid border-blueGray-200 rounded-b bg-white">
                            <button class="text-red-500 bg-transparent border border-solid border-red-500 hover:bg-red-500 hover:text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-3 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button" v-on:click="toggleModal()">
                                Decline
                            </button>
                            <router-link
                                class="btn btn-blue shadow-sm rounded-md mr-4"
                                to="/tickets/new"
                            >
                                {{ $t('Accept') }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="showModal" class="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </template>
    </div>
</template>

<script>
let slideIndex = 0;
import {mixin as clickaway} from "vue-clickaway";

export default {
    name: "index",
    metaInfo() {
        return {
            title: this.$i18n.t('My import permits')
        }
    },
    mixins: [clickaway],
    mounted() {
        this.getStatuses();
        this.getTickets();

        this.showSlides();
    },
    data() {
        return {
            loading: true,
            showModal: false,
            ticketList: [],
            statusList: [],
            filters: {
                search: '',
                status: null,
            },
            sort: {
                order: 'desc',
                column: 'updated_at',
            },
            page: 1,
            perPage: 10,
            pagination: {
                currentPage: 0,
                perPage: 0,
                total: 0,
                totalPages: 0
            },
        };
    },
    computed: {
        anyFilter() {
            return this.filters.search !== ''
                || this.filters.status !== null
        }
    },
    filters: {
        momentFormatDate: function (value) {
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format);
        },
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        showSlides() {
            let i;
            const slides = document.getElementsByClassName("mySlides");
            const dots = document.getElementsByClassName("dot");
            for (i = 0; i < slides.length; i++) {
                slides[i].style.display = "none";
            }
            slideIndex++;
            if (slideIndex > slides.length) { slideIndex = 1 }
            for (i = 0; i < dots.length; i++) {
                dots[i].className = dots[i].className.replace(" active", "");
            }
            slides[slideIndex - 1].style.display = "block";
            dots[slideIndex - 1].className += " active";
            setTimeout(this.showSlides, 5000); // Change image every 2 seconds
        },
        getStatuses() {
            const self = this;
            axios.get('api/tickets/statuses').then(function (response) {
                self.statusList = response.data;
            });
        },
        toggleModal: function(){
            this.showModal = !this.showModal;
        },
        changePage(page) {
            const self = this;
            if ((page > 0) && (page <= self.pagination.totalPages) && (page !== self.page)) {
                self.page = page;
                self.getTickets();
            }
        },
        changeSort() {
            const self = this;
            if (self.sort.order === 'asc') {
                self.sort.order = 'desc';
            } else if (self.sort.order === 'desc') {
                self.sort.order = 'asc';
            }
            self.getTickets();
        },
        getTickets() {
            const self = this;
            self.loading = true;
            axios.get('api/tickets', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    perPage: self.perPage,
                    search: self.filters.search,
                    status: self.filters.status,
                }
            }).then(function (response) {
                self.ticketList = response.data.items;
                self.pagination = response.data.pagination;
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getTickets();
                } else {
                    self.loading = false;
                }
            }).catch(function () {
                self.loading = false;
            });
        },
    }
}
</script>
<style>

.gal {background-color:lightgray;}
.gal > * {
    display:inline-block;
    background-color:lightgreen;
}

* {
    box-sizing: border-box;
}

body {
    font-family: Verdana, sans-serif;
}

.mySlides {
    display: none;
}

img {
    vertical-align: middle;
}

/* Slideshow container */
.slideshow-container {
    max-width: 300px;
    max-height: 300px;


    border:3px;
}

/* Caption text */
.text {
    color: #f2f2f2;
    font-size: 15px;
    padding: 8px 12px;
    position: absolute;
    bottom: 8px;
    width: 100%;
    text-align: center;
}

/* Number text (1/3 etc) */
.numbertext {
    color: #f2f2f2;
    font-size: 12px;
    padding: 8px 12px;
    position: absolute;
    top: 0;
}

/* The dots/bullets/indicators */
.dot {
    height: 15px;
    width: 15px;
    margin: 0 2px;

    background-color: #bbb;
    border-radius: 50%;
    display: inline-block;
    transition: background-color 0.6s ease;
}

.active {
    background-color: #717171;
}

/* Fading animation */
.fade {
    -webkit-animation-name: fade;
    -webkit-animation-duration: 1.5s;
    animation-name: fade;
    animation-duration: 1.5s;
}

@-webkit-keyframes fade {
    from {
        opacity: .4
    }

    to {
        opacity: 1
    }
}

@keyframes fade {
    from {
        opacity: .4
    }

    to {
        opacity: 1
    }
}

/* On smaller screens, decrease text size */
@media only screen and (max-width: 300px) {
    .text {
        font-size: 11px
    }
}
</style>
